import { useEffect } from 'react';
import { useEditor } from 'tldraw';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useBoardStore } from '../store/useBoardStore';
import {
  digestDeleteListenersSuppressed,
  enqueueDigestAddFromStore,
} from '../utils/boardConstraintsDigest';
import { canvasDocumentHydrating } from '../utils/canvasHydration';
import {
  arrowRelationDigestDecision,
  arrowRelationKey,
  buildArrowRelationAddEvent,
  readArrowRelation,
} from '../utils/arrowRelationDigest';

const RELATION_DIGEST_DEBOUNCE_MS = 400;

function arrowIdsFromRecord(record) {
  if (!record) return [];
  if (record.typeName === 'shape' && record.type === 'arrow') return [record.id];
  if (record.typeName === 'binding' && record.type === 'arrow' && record.fromId) {
    return [record.fromId];
  }
  return [];
}

/**
 * Fold labeled, both-ends-bound connecting lines into the constraints ledger.
 * Lines already on the board are remembered and do not mark Do-next stale.
 * Unlabeled / unbound lines are ignored. A later edit, delete, or unbind updates the claim.
 */
export default function ArrowRelationDigest() {
  const editor = useEditor();
  const { enqueueDigestDelete } = useBoardAiContext();
  const canEdit = useBoardStore(s => Boolean(s.canEdit || s.isOwner));

  useEffect(() => {
    if (!editor || !canEdit) return undefined;

    const timers = new Map();
    const committedKeys = new Map();

    function relationKey(shapeId) {
      return arrowRelationKey(readArrowRelation(editor, editor.getShape(shapeId)));
    }

    function forgetTimer(shapeId) {
      clearTimeout(timers.get(shapeId));
      timers.delete(shapeId);
    }

    function commit(shapeId) {
      const nextKey = relationKey(shapeId);
      const prevKey = committedKeys.get(shapeId) || '';
      const decision = arrowRelationDigestDecision(prevKey, nextKey);
      if (decision === 'ignore' || decision === 'remember' || decision === 'forget') {
        if (decision === 'forget') committedKeys.delete(shapeId);
        return;
      }
      if (decision === 'revoke') {
        if (!digestDeleteListenersSuppressed.active) {
          enqueueDigestDelete?.([], [shapeId]);
        }
        committedKeys.delete(shapeId);
        return;
      }
      const shape = editor.getShape(shapeId);
      const event = buildArrowRelationAddEvent(editor, shape);
      if (!event) return;
      enqueueDigestAddFromStore([event]);
      committedKeys.set(shapeId, nextKey);
    }

    function schedule(shapeId) {
      clearTimeout(timers.get(shapeId));
      timers.set(
        shapeId,
        setTimeout(() => {
          timers.delete(shapeId);
          commit(shapeId);
        }, RELATION_DIGEST_DEBOUNCE_MS)
      );
    }

    function baseline(shapeId) {
      forgetTimer(shapeId);
      const nextKey = relationKey(shapeId);
      const decision = arrowRelationDigestDecision(
        committedKeys.get(shapeId) || '',
        nextKey,
        { baseline: true }
      );
      if (decision === 'remember') committedKeys.set(shapeId, nextKey);
      else committedKeys.delete(shapeId);
    }

    function arrowIdsIn(changes) {
      const ids = new Set();
      for (const record of Object.values(changes?.removed || {})) {
        for (const arrowId of arrowIdsFromRecord(record)) ids.add(arrowId);
      }
      for (const record of Object.values(changes?.added || {})) {
        for (const arrowId of arrowIdsFromRecord(record)) ids.add(arrowId);
      }
      for (const tuple of Object.values(changes?.updated || {})) {
        const next = Array.isArray(tuple) ? tuple[1] : tuple;
        for (const arrowId of arrowIdsFromRecord(next)) ids.add(arrowId);
      }
      return ids;
    }

    const unlisten = editor.store.listen(
      (entry) => {
        const changes = entry?.changes || {};
        if (canvasDocumentHydrating.active) {
          for (const arrowId of arrowIdsIn(changes)) baseline(arrowId);
          return;
        }

        const removedArrowIds = new Set();

        for (const record of Object.values(changes.removed || {})) {
          if (record?.typeName === 'shape' && record.type === 'arrow') {
            forgetTimer(record.id);
            if (committedKeys.has(record.id)) {
              if (!digestDeleteListenersSuppressed.active) {
                enqueueDigestDelete?.([], [record.id]);
              }
              committedKeys.delete(record.id);
            }
            continue;
          }
          for (const arrowId of arrowIdsFromRecord(record)) {
            removedArrowIds.add(arrowId);
          }
        }

        const dirty = new Set(removedArrowIds);
        for (const record of Object.values(changes.added || {})) {
          for (const arrowId of arrowIdsFromRecord(record)) dirty.add(arrowId);
        }
        for (const tuple of Object.values(changes.updated || {})) {
          const next = Array.isArray(tuple) ? tuple[1] : tuple;
          for (const arrowId of arrowIdsFromRecord(next)) dirty.add(arrowId);
        }

        for (const arrowId of dirty) {
          if (!editor.getShape(arrowId)) continue;
          const nextKey = relationKey(arrowId);
          const decision = arrowRelationDigestDecision(
            committedKeys.get(arrowId) || '',
            nextKey
          );
          if (decision === 'enqueue' || decision === 'revoke') schedule(arrowId);
        }
      },
      { scope: 'document' }
    );

    function seedExisting() {
      for (const shape of editor.getCurrentPageShapes() || []) {
        if (shape?.type !== 'arrow') continue;
        baseline(shape.id);
      }
    }

    seedExisting();

    return () => {
      unlisten();
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [editor, canEdit, enqueueDigestDelete]);

  return null;
}
